import { MAX_SELFIE_BASE64_BYTES } from "@word-duel/shared";
import type { SelfieRecord } from "./api";

const MAX_EDGE = 960;
const MIN_EDGE = 320;

async function loadSource(
  file: File
): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if ("createImageBitmap" in window) {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close()
    };
  }

  const url = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = "async";
  image.src = url;
  await image.decode();
  return {
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    close: () => URL.revokeObjectURL(url)
  };
}

function canvasBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the processed photo."));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      if (comma < 0) reject(new Error("Could not encode the processed photo."));
      else resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(blob);
  });
}

export async function compressSelfie(file: File): Promise<SelfieRecord> {
  if (!file.type.startsWith("image/")) throw new Error("Choose a JPEG, PNG or WebP image.");
  const loaded = await loadSource(file);
  try {
    const initialScale = Math.min(1, MAX_EDGE / Math.max(loaded.width, loaded.height));
    let width = Math.max(1, Math.round(loaded.width * initialScale));
    let height = Math.max(1, Math.round(loaded.height * initialScale));
    let quality = 0.74;
    let mimeType: "image/webp" | "image/jpeg" = "image/webp";

    while (true) {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) throw new Error("This browser cannot process the photo.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(loaded.source, 0, 0, width, height);

      let blob = await canvasBlob(canvas, mimeType, quality);
      if (!blob || blob.type !== mimeType) {
        mimeType = "image/jpeg";
        blob = await canvasBlob(canvas, mimeType, quality);
      }
      if (!blob) throw new Error("This browser could not encode the photo.");

      const base64 = await blobToBase64(blob);
      if (base64.length <= MAX_SELFIE_BASE64_BYTES || Math.max(width, height) <= MIN_EDGE) {
        if (base64.length > MAX_SELFIE_BASE64_BYTES) {
          throw new Error("The photo is still too large. Please choose a simpler image.");
        }
        return { mimeType, base64, width, height };
      }

      if (quality > 0.46) quality = Math.max(0.46, quality - 0.08);
      else {
        width = Math.max(1, Math.round(width * 0.84));
        height = Math.max(1, Math.round(height * 0.84));
        quality = 0.68;
      }
    }
  } finally {
    loaded.close();
  }
}

export function selfieDataUrl(selfie: SelfieRecord): string {
  return `data:${selfie.mimeType};base64,${selfie.base64}`;
}
